import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
    DialogHeader,
    DialogFooter,
    DialogClose,
} from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ESingleSelectPicker from '@/components/everridge/ESingleSelectPicker';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import { useTenantsSelectOptionsApi } from '@/features/superAdmin/features/tenants/hooks/useTenantsSelectOptionsApi';
import {
    SuperAdminCatalogPageFilterFormSchema,
    SuperAdminCatalogPageFilterFormData,
} from '@/features/superAdmin/features/data/shared/validators/SuperAdminCatalogPageFilterValidator';
import { useSuperAdminDataOwnersLookupApi } from '@/features/superAdmin/features/data/shared/hooks/useSuperAdminDataLookupsApi';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SuperAdminCatalogFilterState } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type Props = {
    onClose: () => void;
    isMobile?: boolean;
    showOwnerFilter?: boolean;
    filter: SuperAdminCatalogFilterState;
    setFilter: (filter: Partial<SuperAdminCatalogFilterState>) => void;
    resetFilter: () => void;
};

const SuperAdminCatalogPageFilter: FC<Props> = ({
    onClose,
    isMobile,
    showOwnerFilter = false,
    filter,
    setFilter,
    resetFilter,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();
    const { tenantsSelectData } = useTenantsSelectOptionsApi();

    const { handleSubmit, reset, watch, setValue, register } =
        useForm<SuperAdminCatalogPageFilterFormData>({
            resolver: zodResolver(SuperAdminCatalogPageFilterFormSchema),
            values: {
                tenantId: filter.tenantId,
                ownerId: filter.ownerId,
                searchText: filter.searchText,
            },
        });

    const tenantIdValue = watch('tenantId');
    const ownerIdValue = watch('ownerId');

    const { data: ownersData } = useSuperAdminDataOwnersLookupApi(tenantIdValue);

    const tenantSelectOptions = useMemo(
        () =>
            (tenantsSelectData?.items ?? []).map((tenant) => ({
                value: tenant.id,
                label: tenant.name ?? tenant.id,
            })),
        [tenantsSelectData?.items],
    );

    const ownerSelectOptions = useMemo(
        () =>
            (ownersData?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [ownersData?.items],
    );

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const onSubmit = (data: SuperAdminCatalogPageFilterFormData) => {
        setFilter({
            ...data,
            skip: 0,
        });
        handleClose();
    };

    const handleReset = () => {
        resetFilter();
        reset({
            tenantId: null,
            ownerId: null,
            searchText: null,
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className={cn(isMobile && 'max-h-[90vh] overflow-y-auto')}>
                <DialogHeader>
                    <DialogTitle>{t('superAdmin.data.filters.title')}</DialogTitle>
                    <DialogDescription>{t('superAdmin.data.filters.catalogDescription')}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label>{t('superAdmin.data.filters.tenant')}</Label>
                        <ESingleSelectPicker
                            value={tenantIdValue}
                            onChange={(value) => {
                                setValue('tenantId', value);
                                setValue('ownerId', null);
                            }}
                            options={tenantSelectOptions}
                            placeholder={t('superAdmin.data.filters.tenantPlaceholder')}
                            allOptionLabel={t('superAdmin.data.filters.allTenants')}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="searchText">{t('superAdmin.data.filters.search')}</Label>
                        <Input id="searchText" {...register('searchText')} />
                    </div>
                    {showOwnerFilter && (
                        <div className="flex flex-col gap-2">
                            <Label>{t('superAdmin.data.filters.owner')}</Label>
                            <ESingleSelectPicker
                                value={ownerIdValue}
                                onChange={(value) => setValue('ownerId', value)}
                                options={ownerSelectOptions}
                                placeholder={t('superAdmin.data.filters.ownerPlaceholder')}
                                allOptionLabel={t('superAdmin.data.filters.allOwners')}
                                disabled={!tenantIdValue}
                            />
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={handleReset}>
                            {t('elements.buttons.reset')}
                        </Button>
                        <DialogClose asChild>
                            <Button type="button" variant="outline" className="gap-2">
                                <Cancel_01_Icon className="h-4 w-4" />
                                {t('elements.buttons.cancel')}
                            </Button>
                        </DialogClose>
                        <Button type="submit">{t('elements.buttons.apply')}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default SuperAdminCatalogPageFilter;
